import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard } from "swiper/modules";
import "../shared.css";
import "swiper/css";
import "swiper/css/navigation";

const ProductModal = lazy(() => import("./ProductModal"));

const API_BASE = import.meta.env.VITE_API_URL || "";

const formatToSlug = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const groupProductsByName = (products) => {
  const grouped = {};
  for (const product of products) {
    if (!grouped[product.item_name]) {
      grouped[product.item_name] = [];
    }
    grouped[product.item_name].push(product);
  }
  return grouped;
};

export default function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);
  const swiperRef = useRef(null);
  const maxVisible = 5;

  useEffect(() => {
    fetch(`${API_BASE}/api/products`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch products");
        return res.json();
      })
      .then((data) => {
        setProducts(data);
        setGrouped(groupProductsByName(data));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
        setError("Could not load products.");
        setLoading(false);
      });
  }, []);

  const categories = [
    ...new Set(
      products
        .map((product) => product.category)
        .filter((cat) => cat && cat !== "Commission")
    ),
  ];

  const filteredGrouped = Object.entries(grouped)
    .filter(([_, variations]) =>
      variations.every((v) => v.category !== "Commission")
    )
    .filter(([_, variations]) =>
      activeCategory ? variations.some((v) => v.category === activeCategory) : true
    );

  const total = filteredGrouped.length;

  const goPrev = () => {
    const newIndex = (modalIndex - 1 + total) % total;
    setModalIndex(newIndex);
    setSelectedGroup(filteredGrouped[newIndex][1]);
  };

  const goNext = () => {
    const newIndex = (modalIndex + 1) % total;
    setModalIndex(newIndex);
    setSelectedGroup(filteredGrouped[newIndex][1]);
  };

  const renderPagination = () => {
    const currentPage = Math.floor(currentIndex / maxVisible);
    const start = currentPage * maxVisible;
    const end = Math.min(start + maxVisible, total);

    return (
      <div className="featured-pagination" role="navigation" aria-label="Product pagination">
        {Array.from({ length: end - start }, (_, i) => {
          const index = start + i;
          const isActive = index === currentIndex;

          return (
            <button
              key={index}
              className={`bullet ${isActive ? "active" : ""}`}
              onClick={() => swiperRef.current?.slideToLoop(index)}
              aria-label={
                isActive
                  ? `Current product group ${index + 1}`
                  : `Go to product group ${index + 1}`
              }
              aria-current={isActive ? "true" : undefined}
              role="button"
              type="button"
            />
          );
        })}
      </div>
    );
  };

  const renderImageSrc = (product) => {
    const slug = formatToSlug(`${product.variation_name}-${product.item_name}`);
    return `/images/products/${slug}.jpg`;
  };

  const handleImgError = (e) => {
    e.target.src = "/images/products/placeholder.jpg";
  };

  if (loading)
    return (
      <div aria-live="polite">
        <p className="text-center text-white">Loading products...</p>
      </div>
    );

  if (error)
    return <p className="text-center text-red-500">{error}</p>;

  const isFiltered = !!activeCategory;

  return (
    <section className="featured-products">
      <h2 className="featured-header">Featured Products</h2>
      <p className="featured-subtext">All of our products are lovingly handmade</p>
      <p className="featured-subtext">Images are for reference only, and slight variations are part of the magic.</p>

      <div className="filter-bar">
        <button
          onClick={() => setActiveCategory(null)}
          className={activeCategory === null ? "active" : ""}
        >
          All Items
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            className={activeCategory === cat ? "active" : ""}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {!isFiltered && filteredGrouped.length > 0 && (
        <Swiper
          onSwiper={(swiperInstance) => {
            swiperRef.current = swiperInstance;
          }}
          onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
          centeredSlides={false}
          loop
          watchOverflow
          navigation
          keyboard={{ enabled: true }}
          modules={[Navigation, Keyboard]}
          className="featured-swiper"
          breakpoints={{
            0: { slidesPerView: 1, spaceBetween: 16 },
            640: { slidesPerView: 2, spaceBetween: 20 },
            1024: { slidesPerView: 3, spaceBetween: 25 },
            1280: { slidesPerView: 4, spaceBetween: 30 },
            1440: { slidesPerView: 4, spaceBetween: 35 },
            1536: { slidesPerView: 5, spaceBetween: 40 },
            1920: { slidesPerView: 5, spaceBetween: 40 },
            2560: { slidesPerView: 7, spaceBetween: 50 },
          }}
        >
          {filteredGrouped.map(([name, variations], index) => (
            <SwiperSlide key={name}>
              <div
                className="product-card"
                onClick={(e) => {
                  e.currentTarget.focus();
                  setSelectedGroup(variations);
                  setModalIndex(index);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedGroup(variations);
                    setModalIndex(index);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${name}, ${variations[0].variation_name}`}
              >
                <img
                  src={renderImageSrc(variations[0])}
                  onError={handleImgError}
                  alt={`${name}, ${variations[0].variation_name}`}
                />
                <h3>{name}</h3>
                <p>{variations[0].description}</p>
                <p className="product-modal-price">
                  {variations[0].price}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {isFiltered && (
        <div className="filtered-grid">
          {filteredGrouped.map(([name, variations], index) => (
            <div
              key={name}
              className="product-card"
              onClick={() => {
                setSelectedGroup(variations);
                setModalIndex(index);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedGroup(variations);
                  setModalIndex(index);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`View details for ${name}, ${variations[0].variation_name}`}
            >
              <img
                src={renderImageSrc(variations[0])}
                onError={handleImgError}
                alt={`${name}, ${variations[0].variation_name}`}
              />
              <h3>{name}</h3>
              <p>{variations[0].description}</p>
              <p className="product-modal-price">
                {variations[0].price}
              </p>
            </div>
          ))}
        </div>
      )}

      {!isFiltered && renderPagination()}

      {selectedGroup && (
        <Suspense fallback={<div className="modal-loading">Loading…</div>}>
          <ProductModal
            productGroup={selectedGroup}
            onClose={() => {
              setSelectedGroup(null);
              setModalIndex(null);
              try {
                swiperRef.current?.keyboard?.enable();
              } catch (e) {
                console.warn("Swiper keyboard enable failed", e);
              }
              document.body.style.overflow = "auto";
            }}
            onPrev={goPrev}
            onNext={goNext}
          />
        </Suspense>
      )}
    </section>
  );
}
